import { useEffect, useRef, useState } from 'react';
import type { MenuItem, Table, TableProduct } from '../../types';
import { areTablesEqual, cloneTable } from '../../utils/tableState';
import { equalsNormalizedSearchText } from '../../utils/textSearch';

interface UseTableModalStateOptions {
  table: Table;
  menuItems: MenuItem[];
  onAutoSave?: (table: Table) => void;
}

function normalizeTableAgainstCatalog(table: Table, menuItems: MenuItem[]): Table {
  if (!Array.isArray(table.products) || table.products.length === 0 || menuItems.length === 0) {
    return cloneTable(table);
  }

  const catalogProductById = new Map(menuItems.map((item) => [
    String(item.id),
    { name: item.name, price: typeof item.price === 'number' ? item.price : null },
  ]));

  return {
    ...cloneTable(table),
    products: table.products.map((product) => {
      const catalogProduct = catalogProductById.get(String(product.itemId));
      if (!catalogProduct) {
        return { ...product };
      }

      const nextName = catalogProduct.name;
      const nextUnitPrice = catalogProduct.price;
      if (nextName === product.name && (product.unitPrice ?? null) === nextUnitPrice) {
        return { ...product };
      }

      return {
        ...product,
        name: nextName,
        unitPrice: nextUnitPrice,
      };
    }),
  };
}

function syncCatalogNamesIntoTable(table: Table, menuItems: MenuItem[]): Table {
  return normalizeTableAgainstCatalog(table, menuItems);
}

function findCatalogMatch(menuItems: MenuItem[], typedValue: string): MenuItem | undefined {
  const typedName = typedValue.trim();
  if (!typedName) {
    return undefined;
  }

  return menuItems.find((item) => equalsNormalizedSearchText(item.name, typedName));
}

export function useTableModalState({ table, menuItems, onAutoSave }: UseTableModalStateOptions) {
  const [editedTable, setEditedTable] = useState<Table>(() => normalizeTableAgainstCatalog(table, menuItems));
  const [newProductName, setNewProductName] = useState<string>('');
  const [newProductNote, setNewProductNote] = useState<string>('');
  const [newProductQty, setNewProductQty] = useState<number>(1);
  const [newProductHandling, setNewProductHandling] = useState<boolean>(false);
  const [newProductDelivered, setNewProductDelivered] = useState<boolean>(false);
  const [newProductPaid, setNewProductPaid] = useState<boolean>(false);
  const [copiedProducts, setCopiedProducts] = useState<TableProduct[] | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const suppressNextAutoSave = useRef<boolean>(true);
  const autoSaveTimeoutRef = useRef<number | null>(null);
  const hasMountedRef = useRef<boolean>(false);
  const previousTableIdRef = useRef<string>(table.id);
  const previousIncomingTableRef = useRef<Table>(cloneTable(table));
  const editedTableRef = useRef<Table>(editedTable);
  const onAutoSaveRef = useRef<typeof onAutoSave>(onAutoSave);

  useEffect(() => {
    editedTableRef.current = editedTable;
  }, [editedTable]);

  useEffect(() => {
    onAutoSaveRef.current = onAutoSave;
  }, [onAutoSave]);

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      previousTableIdRef.current = table.id;
      previousIncomingTableRef.current = cloneTable(table);
      return;
    }

    const nextEditedTable = normalizeTableAgainstCatalog(table, menuItems);
    const switchedTable = previousTableIdRef.current !== table.id;
    const incomingTableChanged = !areTablesEqual(previousIncomingTableRef.current, table);
    previousTableIdRef.current = table.id;
    previousIncomingTableRef.current = cloneTable(table);

    if (switchedTable) {
      suppressNextAutoSave.current = true;
      setEditedTable(nextEditedTable);
      setNewProductName('');
      setNewProductNote('');
      setNewProductQty(1);
      setNewProductHandling(false);
      setNewProductDelivered(false);
      setNewProductPaid(false);
      setCopiedProducts(null);
      return;
    }

    if (incomingTableChanged && !areTablesEqual(editedTableRef.current, nextEditedTable)) {
      suppressNextAutoSave.current = true;
      setEditedTable(nextEditedTable);
      return;
    }

    const catalogSyncedTable = syncCatalogNamesIntoTable(editedTableRef.current, menuItems);
    if (!areTablesEqual(editedTableRef.current, catalogSyncedTable)) {
      suppressNextAutoSave.current = true;
      setEditedTable(catalogSyncedTable);
    }
  }, [table, menuItems]);

  useEffect(() => {
    if (!onAutoSaveRef.current) {
      return;
    }
    if (suppressNextAutoSave.current) {
      suppressNextAutoSave.current = false;
      return;
    }

    if (autoSaveTimeoutRef.current !== null) {
      window.clearTimeout(autoSaveTimeoutRef.current);
    }

    autoSaveTimeoutRef.current = window.setTimeout(() => {
      onAutoSaveRef.current?.({
        ...editedTable,
        products: Array.isArray(editedTable.products)
          ? editedTable.products.map(product => ({ ...product }))
          : [],
      });
    }, 250);

    return () => {
      if (autoSaveTimeoutRef.current !== null) {
        window.clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [editedTable]);

  function buildPendingProduct(): TableProduct | null {
    const typedName = newProductName.trim();
    const typedNote = newProductNote.trim();
    const qty = parseInt(String(newProductQty));
    if (!typedName) return null;
    const match = findCatalogMatch(menuItems, typedName);
    return {
      id: Date.now() + Math.random(),
      itemId: match ? match.id : typedName,
      name: match ? match.name : typedName,
      unitPrice: match && typeof match.price === 'number' ? match.price : null,
      note: typedNote || undefined,
      qty: isNaN(qty) || qty < 1 ? 1 : qty,
      handling: newProductHandling,
      delivered: newProductDelivered,
      paid: newProductPaid,
    };
  }

  function appendProduct(product: TableProduct): void {
    setEditedTable(prev => ({
      ...prev,
      products: prev.products ? [...prev.products, product] : [product],
    }));
  }

  function addPendingProduct(): void {
    const product = buildPendingProduct();
    if (!product) return;
    appendProduct(product);
    setNewProductName('');
    setNewProductNote('');
    setNewProductQty(1);
    setNewProductHandling(false);
    setNewProductDelivered(false);
    setNewProductPaid(false);
  }

  function updateTable(patch: Partial<Table>): void {
    setEditedTable(prev => {
      return { ...prev, ...patch };
    });
  }

  function resetTableColor(): void {
    setEditedTable(prev => {
      const next = { ...prev };
      delete next.color;
      return next;
    });
  }

  function removeProduct(index: number): void {
    setEditedTable(prev => {
      const products = prev.products ? prev.products.slice() : [];
      products.splice(index, 1);
      return { ...prev, products };
    });
  }

  function removeAllProducts(): void {
    setEditedTable(prev => ({ ...prev, products: [] }));
  }

  function toggleHandling(index: number): void {
    setEditedTable(prev => {
      const products = prev.products ? prev.products.slice() : [];
      products[index] = { ...products[index], handling: !products[index].handling };
      return { ...prev, products };
    });
  }

  function toggleDelivered(index: number): void {
    setEditedTable(prev => {
      const products = prev.products ? prev.products.slice() : [];
      products[index] = { ...products[index], delivered: !products[index].delivered };
      return { ...prev, products };
    });
  }

  function togglePaid(index: number): void {
    setEditedTable(prev => {
      const products = prev.products ? prev.products.slice() : [];
      products[index] = { ...products[index], paid: !products[index].paid };
      return { ...prev, products };
    });
  }

  function editProductQty(index: number, value: number): void {
    const qtyValue = parseInt(String(value));
    setEditedTable(prev => {
      const products = prev.products ? prev.products.slice() : [];
      products[index] = {
        ...products[index],
        qty: isNaN(qtyValue) || qtyValue < 1 ? 1 : qtyValue,
      };
      return { ...prev, products };
    });
  }

  function editProductName(index: number, value: string): void {
    const typed = value.trim();
    setEditedTable(prev => {
      const products = prev.products ? prev.products.slice() : [];
      const match = findCatalogMatch(menuItems, typed);
      products[index] = {
        ...products[index],
        name: match ? match.name : typed || products[index].name,
        itemId: match ? match.id : typed || products[index].itemId,
        unitPrice: match && typeof match.price === 'number' ? match.price : null,
      };
      return { ...prev, products };
    });
  }

  function handleNewProductNameChange(value: string): void {
    const match = findCatalogMatch(menuItems, value);
    setNewProductName(match ? match.name : value);
  }

  function editProductNote(index: number, value: string): void {
    setEditedTable(prev => {
      const products = prev.products ? prev.products.slice() : [];
      products[index] = {
        ...products[index],
        note: value.trim() ? value : '',
      };
      return { ...prev, products };
    });
  }

  function startRowDrag(index: number): void {
    setDraggedIndex(index);
    setDragOverIndex(null);
  }

  function dragOverRow(index: number, e: React.DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    setDragOverIndex(index);
  }

  function dropRow(dropIndex: number): void {
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }
    setEditedTable(prev => {
      const products = prev.products ? prev.products.slice() : [];
      const [item] = products.splice(draggedIndex, 1);
      products.splice(dropIndex, 0, item);
      return { ...prev, products };
    });
    setDraggedIndex(null);
    setDragOverIndex(null);
  }

  return {
    editedTable,
    newProductName,
    newProductNote,
    newProductQty,
    newProductHandling,
    newProductDelivered,
    newProductPaid,
    copiedProducts,
    dragOverIndex,
    setNewProductName: handleNewProductNameChange,
    setNewProductNote,
    setNewProductQty,
    setNewProductHandling,
    setNewProductDelivered,
    setNewProductPaid,
    setCopiedProducts,
    buildPendingProduct,
    addPendingProduct,
    appendProduct,
    updateTable,
    resetTableColor,
    removeProduct,
    removeAllProducts,
    toggleHandling,
    toggleDelivered,
    togglePaid,
    editProductQty,
    editProductName,
    editProductNote,
    startRowDrag,
    dragOverRow,
    dropRow,
  };
}
